import type { AxiosRequestConfig } from 'axios';
import z from 'zod';
import { api } from '../../axios.instance';
import { responseContract, type PagePaginationRequestDto } from '../../lib';

const FacilityLevelsOfCareDtoSchema = z.object({
  id: z.number(),
  levelOfCare: z.number(),
  createdFlags: z.number(),
  activeAlerts: z.number(),
  createdAlerts: z.number(),
  residents: z.object({
    currentAvg: z.number(),
    changePct: z.number().nullable(),
  }),
  rate: z.number().nullable(),
  totalRevenue: z.object({
    currentAvg: z.number(),
    changePct: z.number().nullable(),
  }),
});

const ChartDatasetSchema = z.object({
  levelOfCare: z.number(),
  data: z.array(z.number()),
});

const LevelsOfCareChartSchema = z.object({
  labels: z.array(z.string()),
  datasets: z.array(ChartDatasetSchema),
  granularity: z.enum(['day', 'week', 'month']),
});

const GetFacilityLevelsOfCareResponseDtoSchema = z.object({
  data: z.array(FacilityLevelsOfCareDtoSchema),
  chart: LevelsOfCareChartSchema,
  meta: z.object({
    page: z.number(),
    perPage: z.number(),
    total: z.number(),
    totalPages: z.number(),
  }),
});

export type GetFacilityLevelsOfCareRequestDto = PagePaginationRequestDto<
  {
    /** Omit for the All Facilities view. */ facilityId?: string;
    from: string;
    to: string;
  },
  | 'levelOfCare'
  | 'createdFlags'
  | 'activeAlerts'
  | 'createdAlerts'
  | 'residents'
  | 'rate'
  | 'totalRevenue'
>;
export type GetFacilityLevelsOfCareResponseDto = z.infer<
  typeof GetFacilityLevelsOfCareResponseDtoSchema
>;
export type FacilityLevelsOfCareDto = z.infer<
  typeof FacilityLevelsOfCareDtoSchema
>;
export type LevelsOfCareChartDto = z.infer<typeof LevelsOfCareChartSchema>;

export const getFacilityLevelsOfCare = (
  params: GetFacilityLevelsOfCareRequestDto,
  config?: AxiosRequestConfig<GetFacilityLevelsOfCareRequestDto>
) => {
  const { facilityId, ...restParams } = params;

  return api
    .get(
      facilityId !== undefined
        ? `/facilities/${facilityId}/levels-of-care`
        : '/facilities/levels-of-care',
      { params: restParams, ...config }
    )
    .then(responseContract(GetFacilityLevelsOfCareResponseDtoSchema));
};
